import type { RuleDetailTabKey } from "../workspace/ruleDetailTabs";
import { NEW_RULE_PARAM_VALUE } from "./ruleViewParams";

/**
 * The query params an embedded surface writes onto its host's URL. They are
 * namespaced away from the standalone surface's `rule`/`tab` because the host
 * route owns its own query string — an experience id, its own `tab`, a
 * component id — and the rule view must be additive to all of it.
 */
export const EMBEDDED_RULE_PARAM = "alRule";
export const EMBEDDED_RULE_TAB_PARAM = "alTab";
export const EMBEDDED_SESSION_PARAM = "alSession";

export interface EmbeddedPathParams {
  ruleParam: string;
  tabParam: string;
  sessionParam: string;
}

export interface EmbeddedPaths {
  rulePath: (ruleId: string, tab?: RuleDetailTabKey) => string;
  newRulePath: string;
  newRuleSessionPath: (sessionId: string) => string;
  /** The host's URL with the rule params removed — where "back" lands. */
  exitPath: string;
}

const withParams = (
  pathname: string,
  params: URLSearchParams,
  mutate: (next: URLSearchParams) => void
) => {
  const next = new URLSearchParams(params);
  mutate(next);
  const search = next.toString();
  // No `?` at all when nothing is left, so exiting restores the host's URL
  // byte for byte. Leave guards compare search strings, and a trailing `?`
  // reads as a change to them.
  return search ? `${pathname}?${search}` : pathname;
};

/**
 * The three URLs an embedded agentic-logic surface needs, derived from the
 * HOST's own location — the one captured before any rule param was added.
 * Deriving from the live location instead would fold each view's params into
 * the next one's base and compound them.
 */
export const deriveEmbeddedPaths = (
  hostPathname: string,
  hostSearch: string,
  { ruleParam, tabParam, sessionParam }: EmbeddedPathParams
): EmbeddedPaths => {
  const host = new URLSearchParams(hostSearch);

  // The session param belongs to the creation view alone: every other view
  // drops it, so a rule or the host never carries a conversation it is not.
  return {
    rulePath: (ruleId, tab) =>
      withParams(hostPathname, host, (next) => {
        next.set(ruleParam, ruleId);
        if (tab) {
          next.set(tabParam, tab);
        } else {
          next.delete(tabParam);
        }
        next.delete(sessionParam);
      }),
    newRulePath: withParams(hostPathname, host, (next) => {
      next.set(ruleParam, NEW_RULE_PARAM_VALUE);
      next.delete(tabParam);
      next.delete(sessionParam);
    }),
    newRuleSessionPath: (sessionId) =>
      withParams(hostPathname, host, (next) => {
        next.set(ruleParam, NEW_RULE_PARAM_VALUE);
        next.delete(tabParam);
        next.set(sessionParam, sessionId);
      }),
    exitPath: withParams(hostPathname, host, (next) => {
      next.delete(ruleParam);
      next.delete(tabParam);
      next.delete(sessionParam);
    }),
  };
};
